/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable react/jsx-handler-names,react/jsx-pascal-case */
import {type FormEvent, useRef} from 'react';

import {Col, media, Row, Spacer, spacing, translucify} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import Image from 'next/image';
import Link from 'next/link';
import {useRouter} from 'next/router';
import {useTranslation} from 'next-i18next';
import styled from 'styled-components';

import {HoverInfoPopup} from 'UI/components/action/HoverInfoPopup';
import {Button} from 'UI/components/action/ultra/Button';
import type {RadioGroupItem} from 'UI/components/action/ultra/RadioGroup';
import {RadioGroup} from 'UI/components/action/ultra/RadioGroup';
import {Slider} from 'UI/components/action/ultra/Slider';
import {Eyebrow} from 'UI/components/layout/ultra/Eyebrow';
import {Headline} from 'UI/components/layout/ultra/Headline';
import {HGroup} from 'UI/components/layout/ultra/HGroup';
import {Text} from 'UI/components/layout/ultra/Text';

import {orderDataService} from 'Application/services/ultra/OrderDataService';
import {featureIconMap} from 'UI/modules/admin/EditProduct/Features';

import {
    CCTEVOSlide01,
    CCTEVOSlide02,
    CCTEVOSlide03,
    CCTEVOSlide04,
    CCTEVOSlide05,
    CCTEVOSlide06, CCTEVOSlide07,
    CCTEVOSlide08
} from 'Images/ultra';

/**
 * Custom hook to retrieve the translated options for the overview form.
 * It provides options for frame size, color, and cockpit.
 *
 * @returns An object containing options for frame size, color, and cockpit.
 */
const useOverviewFormOptions = () => {
    const {t} = useTranslation('ultra');

    const FrameSizeOptions = [
        {
            label: t('frameSizes.46'),
            value: '46'
        },
        {
            label: t('frameSizes.48'),
            value: '48'
        },
        {
            label: t('frameSizes.51'),
            value: '51'
        },
        {
            label: t('frameSizes.54'),
            value: '54'
        },
        {
            label: t('frameSizes.57'),
            value: '57'
        },
        {
            label: t('frameSizes.60'),
            value: '60'
        },
        {
            label: t('frameSizes.na'),
            value: 'na'
        }
    ];

    const ColorOptions: RadioGroupItem[] = [
        {
            label: value => (
                <HoverInfoPopup content={t(`colors.${value as 'black'}`)}>
                    <Color $color="#3E3E3E" />
                </HoverInfoPopup>
            ),
            value: 'black'
        },
        {
            label: value => (
                <HoverInfoPopup content={t(`colors.${value as 'silver'}`)}>
                    <Color $color="#A6A6A6" />
                </HoverInfoPopup>
            ),
            value: 'silver'
        },
        {
            label: value => (
                <HoverInfoPopup content={t(`colors.${value as 'blue'}`)}>
                    <Color $color="#0E62B7" />
                </HoverInfoPopup>
            ),
            value: 'blue'
        },
        {
            label: value => (
                <HoverInfoPopup content={t(`colors.${value as 'copper'}`)}>
                    <Color $color="#B25C42" />
                </HoverInfoPopup>
            ),
            value: 'copper'
        }
    ];

    const CockpitOptions = [
        {
            label: t('cockpitFits.90380'),
            value: '90380'
        },
        {
            label: t('cockpitFits.100400'),
            value: '100400'
        },
        {
            label: t('cockpitFits.110420'),
            value: '110420'
        },
        {
            label: t('cockpitFits.120420'),
            value: '120420'
        },
        {
            label: t('cockpitFits.na'),
            value: 'na'
        }
    ];

    return {
        CockpitOptions,
        ColorOptions,
        FrameSizeOptions
    };
};

/**
 * React component for displaying the overview section.
 * This component renders a section containing images, text, and form inputs related to product overview.
 * It handles form submission and navigation to the order page upon submission.
 *
 * @returns JSX element representing the overview section.
 *
 * @example
 * ```tsx
 * <OverviewSection />
 * ```
 */
export const OverviewSection = observer(() => {
    const router = useRouter();
    const {t} = useTranslation('ultra', {keyPrefix: 'details.overview'});
    const {t: featureT} = useTranslation('ultra', {keyPrefix: 'features'});
    const jumpRef = useRef<(index: number) => void>();

    const features = ['WHEELS_28', 'DI2_READY', 'TAP_HEADTUBE', 'INT_CABLE', 'THRU_AXLE'] as const;

    const {
        CockpitOptions,
        ColorOptions,
        FrameSizeOptions
    } = useOverviewFormOptions();

    /**
     * Handles form submission.
     * Prevents the default form submission action and navigates to the order page.
     *
     * @param e The form event object.
     */
    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();

        void router.push('/cct-evo/checkout');
    };

    /**
     * Handles changes in color selection by the user, updating the order data service with the new color.
     * This function is triggered whenever a user selects a color option for a product. It updates the current color
     * selection in the order data service to reflect the user's choice. Additionally, based on the selected color, the
     * function uses a reference to execute a jump to a specific index in a carousel or list, allowing for visual
     * feedback or navigation corresponding to the color selection. The color parameter is expected to be one of the
     * predefined color strings ('black', 'silver', 'blue', 'copper'), each associated with a specific index for jumping
     * within a UI component.
     *
     * @param color The color selected by the user, expected to be a string value ('black', 'silver', 'blue', 'copper').
     */
    const handleColorChange = (color: string) => {
        orderDataService.setColor(color);

        if (color === 'black') {
            jumpRef.current?.(0);
        } else if (color === 'silver') {
            // eslint-disable-next-line @nfq/no-magic-numbers
            jumpRef.current?.(5);
        } else if (color === 'blue') {
            // eslint-disable-next-line @nfq/no-magic-numbers
            jumpRef.current?.(6);
        } else if (color === 'copper') {
            // eslint-disable-next-line @nfq/no-magic-numbers
            jumpRef.current?.(7);
        }
    };

    const canProceed = orderDataService.canProceedToOrder;
    const isAvailable = orderDataService.color === 'black';

    return (
        <Row hasNoWrap={['lg', 'xl', 'xxl']}>
            <Col>
                <Slider aspectRatio={['1107 / 738', '1107 / 738', '1107 / 738']} jumpRef={jumpRef}>
                    <SliderImage alt="" src={CCTEVOSlide01} />
                    <SliderImage alt="" src={CCTEVOSlide02} />
                    <SliderImage alt="" src={CCTEVOSlide03} />
                    <SliderImage alt="" src={CCTEVOSlide04} />
                    <SliderImage alt="" src={CCTEVOSlide05} />
                    <SliderImage alt="" src={CCTEVOSlide07} />
                    <SliderImage alt="" src={CCTEVOSlide06} />
                    <SliderImage alt="" src={CCTEVOSlide08} />
                </Slider>
            </Col>
            <Spacer x={1} y={{lg: 0, xs: 3}} isNotStretching />
            <Col lg={4} xs={12}>
                <HGroup>
                    <Eyebrow>{t('eyebrow')}</Eyebrow>
                    <Headline>{t('headline')}</Headline>
                </HGroup>
                <Icons>
                    {features.map(feature => {
                        const Icon = featureIconMap[feature as keyof typeof featureIconMap];

                        return (
                            <HoverInfoPopup key={feature} content={featureT(feature)}>
                                <Icon strokeWidth={2.19} />
                            </HoverInfoPopup>
                        );
                    })}
                </Icons>
                <Spacer y={{lg: 16, xs: 8}} isNotStretching />
                <Text>{t('description')}</Text>
                <Spacer y={{lg: 16, xs: 8}} isNotStretching />
                <SpecWrapper>
                    <SpecItem>
                        <SpecKey>{t('specs.material.key')}</SpecKey>
                        <Spacer y={{lg: 4, xs: 2}} />
                        <SpecValue>{t('specs.material.value')}</SpecValue>
                    </SpecItem>
                    <SpecItem>
                        <SpecKey>{t('specs.weight.key')}</SpecKey>
                        <Spacer y={{lg: 4, xs: 2}} />
                        <SpecValue>{t('specs.weight.value')}</SpecValue>
                    </SpecItem>
                </SpecWrapper>
                <Spacer y={{lg: 10, xs: 8}} isNotStretching />
                <form onSubmit={handleSubmit}>
                    <RadioGroup
                        checked={orderDataService.frameSize}
                        items={FrameSizeOptions}
                        label={t('form.frameSizeLabel')}
                        name="framesize"
                        required
                        onChange={orderDataService.setFrameSize}
                    />
                    <Spacer y={{lg: 4, xs: 2}} isNotStretching />
                    <Text>
                        <StyledLink
                            href="/pdf/Rahmengeometrie-CCT-EVO-ULTRA.pdf"
                            locale={false}
                            target="_blank"
                        >{t('form.linkFrameGeometry')}
                        </StyledLink>
                    </Text>
                    <Spacer y={{lg: 10, xs: 8}} isNotStretching />
                    <RadioGroup
                        checked={orderDataService.color}
                        items={ColorOptions}
                        label={t('form.colorLabel')}
                        name="color"
                        required
                        onChange={handleColorChange}
                    />
                    <Spacer y={{lg: 10, xs: 8}} isNotStretching />
                    <RadioGroup
                        checked={orderDataService.cockpit}
                        items={CockpitOptions}
                        label={t('form.cockpitLabel')}
                        name="cockpit"
                        required
                        onChange={orderDataService.setCockpit}
                    />
                    <Spacer y={{lg: 16, xs: 10}} isNotStretching />
                    <PriceWrapper>
                        <PriceIncl>{t('price.explainer')}</PriceIncl>
                        <Spacer y={4} />
                        <Price>{t('price.value')}</Price>
                    </PriceWrapper>
                    <Spacer y={{lg: 12, xs: 6}} isNotStretching />
                    <Button as="button" disabled={!canProceed} type="button" width="full">
                        {isAvailable ? t('cta') : t('cta-sold-out')}
                    </Button>
                </form>
            </Col>
        </Row>
    );
});

const SliderImage = styled(Image)`
    display: block;
    height: 100%;
    object-fit: cover;
    object-position: center;
    width: 100%;
`;

const Icons = styled.div`
    display: flex;
    gap: ${spacing(4)};
    padding-block-start: ${spacing(8)};
`;

const SpecWrapper = styled.dl`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(8)};
    margin: 0;
    padding: 0;

    ${media('lg')} {
        gap: ${spacing(16)};
    }
`;

const SpecItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const SpecKey = styled.dt`
    color: ${({theme}) => theme.colors.ultraPageForeground};
    font: 1.6rem / 1.5 ${({theme}) => theme.fonts.Dinot};

    ${media('md')} {
        font-size: 2rem;
        line-height: 1.3;
    }
`;

const SpecValue = styled.dd`
    color: ${({theme}) => translucify(theme.colors.ultraPageForeground, 50)};
    font: 1.6rem / 1.5 ${({theme}) => theme.fonts.Dinot};
    margin: 0;

    ${media('md')} {
        font-size: 2rem;
        line-height: 1.3;
    }
`;

interface ColorProps {
    $color: string;
}

const Color = styled.div<ColorProps>`
    background-color: ${({$color}) => $color};
    border-radius: 50%;
    height: 22px;
    position: relative;
    width: 22px;

    &::after {
        background-color: #000000;
        border-radius: 22px 0 0 22px;
        content: '';
        display: block;
        height: 22px;
        left: 0;
        position: absolute;
        top: 0;
        width: 11px;
    }
`;

const PriceWrapper = styled.div``;

const PriceIncl = styled.div`
    color: ${({theme}) => translucify(theme.colors.ultraPageForeground, 50)};
    font: 1.6rem / 1.3 ${({theme}) => theme.fonts.Dinot};

    ${media('lg')} {
        font-size: 2rem;
    }
`;

const Price = styled.div`
    color: ${({theme}) => theme.colors.ultraPageForeground};
    font: 700 2.4rem / 1 ${({theme}) => theme.fonts.Dinot};

    ${media('lg')} {
        font: 700 4.8rem / 1 ${({theme}) => theme.fonts.Dinot};
    }
`;

const StyledLink = styled(Link)`
    color: inherit;
    text-underline-offset: .3rem;
    transition: color .18s ease-in-out;

    &:hover {
        color: ${({theme}) => theme.colors.ultraPageForeground};
    }
`;